import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, NodeResizer, Position, useStore, useUpdateNodeInternals } from '@xyflow/react'
import { estimateCredits, type PictureModel } from '../../shared/services/pictureService'
import { WwLoaderFill } from '../../shared/components/WwLoader'
import { MadeWithButton, type MadeWith, type MadeWithFound } from './MadeWith'
import { useFullSizeTurn } from './fullSize'
import { CropBox } from './CropBox'
import type { ComposeRecipe, Rect } from './pictureOps'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The two things that sit on a canvas.
 *
 * Neither uses the tile hover behaviour from the overview — the tilt and the
 * spotlight are for a card you are about to click, and they fight with a thing
 * you are about to drag. What carries over is the colour: every skill keeps the
 * point on the mark's gradient it had in the rail, so a node is recognisably
 * the same object that was dragged out.
 */

export interface ImageNodeData extends Record<string, unknown> {
  /** A library asset. Company photography; permanent. */
  assetId?: string
  /**
   * A file belonging to this project — dropped on the board, or made by a
   * skill. Two fields rather than one id and a kind, because the two live in
   * different stores and asking which one a picture is in should not require
   * reading a second column.
   */
  fileId?: string
  title?: string
  /** Filled in when the canvas opens; never stored, because signed links expire. */
  url?: string
  /**
   * What produced this image, when a skill produced it.
   *
   * The inputs are named the same way anywhere else on the board is — which
   * store, which id — so an image made from a dropped file records that as
   * faithfully as one made from the library.
   */
  from?: { skill: string; inputs: { store: string; id: string }[]; at: string }
  /**
   * A picture that is being made right now.
   *
   * The frame goes onto the board the moment a run starts, so that the wire is
   * drawn before there is anything on the end of it and the arrangement is
   * already settled when the picture lands. Which means an empty frame now has
   * two possible meanings — one is filling, the other is broken — and this is
   * what keeps them apart. They looked identical for a day and cost an hour.
   */
  making?: boolean
  /**
   * Being drawn by a model, rather than uploaded. Both are `making`; only
   * this one gets the WonderWorks loader, because the loader means "a model
   * is making this" and an upload is just a file on its way.
   */
  drawing?: boolean
  /**
   * The run this frame is waiting on, and which of its pictures is this one.
   *
   * Saved with the board, so a frame survives a reload still knowing what it
   * is for. Without them a reload mid-run left a frame with no picture and
   * nothing waiting to fill it -- the picture was made and stored, and the
   * board never showed it.
   */
  runId?: string
  runIndex?: number
  /**
   * The prompt and settings a picture card made this with. Saved with the
   * board and carried by a copy, so the corner button can say how a picture
   * was made wherever it has ended up.
   */
  made?: MadeWith
  /**
   * Handed in by the board, never stored: finds `made`, or for a picture
   * made before it was recorded, looks the run up. Present only on pictures a
   * card made, which is what puts the corner button on them.
   */
  describe?: () => Promise<MadeWithFound | null>
  /** The frame this picture is in (frames.ts). Saved with the board. */
  frame?: string
  /**
   * The shape a placeholder is drawn at while its picture is on its way.
   * Never stored: once the picture is here, the picture has a shape.
   */
  aspect?: number
  /**
   * Handed in by the board, never stored. The full-size link, for when `url`
   * is a smaller copy that would not load; the picture's own proportions,
   * so the box keeps its shape before the picture has arrived (it used to
   * collapse into a strip with the title squashed across it); and a way to
   * ask for fresh links when neither loads -- a signed link lasts two hours
   * and a board can be open for longer.
   */
  fallback?: string
  shape?: number
  /**
   * Handed in by the board, never stored: the full-size file's longer side in
   * pixels, when known. A copy already that large is the original in all but
   * address, and is never swapped for it.
   */
  fullEdge?: number
  onRetry?: () => void
  /**
   * Turned for looking at, in quarter turns: 0, 90, 180 or 270. Saved with
   * the board. The picture itself is never changed -- downloads, Miro and the
   * models all get it the way it was made (Huanrui, 2026-09-30).
   */
  rotate?: number
  /** Turned a quarter: the box's own width / height, as the turn left it. */
  boxShape?: number
  /** Handed in when the store does not know the picture's size: told its shape once it is drawn. */
  onShape?: (shape: number) => void
  /** Marked from the right-click menu: drawn with an amber edge and a star. Saved with the board. */
  mark?: boolean
  /**
   * A frame waiting for an Edit area (pictureOps.ts): how to put
   * the model's picture together with the original once it arrives. Saved
   * with the board, so a reload can finish the job.
   */
  compose?: ComposeRecipe
  /**
   * Being worked on in place -- an Edit area's Apply replacing it. The
   * loader is drawn over the picture until the new one is in. Not saved.
   */
  working?: boolean
  /**
   * A frame that holds, or is waiting for, a VIDEO from a Video card
   * (videoNode.tsx, 2026-10-08). Saved with the board, so a reload waits for
   * it the video's way -- minutes, not the pictures' twelve.
   */
  clip?: boolean
  /** Handed in by the board, never stored: the file is a video, so it is played rather than drawn. */
  video?: boolean
  /** Handed in by the board, never stored: in crop mode (CropBox.tsx). */
  cropping?: boolean
  /** Handed in by the board: the box chosen, in the node's units, and the node's size then. */
  onCrop?: (rect: Rect, box: { w: number; h: number }) => void
  onCropCancel?: () => void
  /**
   * Handed in by the board: the crop box dragged out past the picture's edges
   * (2026-10-26) -- the new picture's box, in the node's units, the node's
   * size then, and what goes round it.
   */
  onExpand?: (rect: Rect, box: { w: number; h: number }, prompt: string) => void
  /** About what one expand costs. */
  expandCredits?: number
  /**
   * Handed in by the board, never stored: the only thing selected, and a way
   * to turn it by so many quarter turns (negative is anticlockwise).
   */
  solo?: boolean
  onRotate?: (quarters: number) => void
}

export interface SkillNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  /** `r, g, b` from the mark's own spectrum, carried over from the rail. */
  glow: string
  /** True between pressing run and the answer arriving. */
  running?: boolean
  error?: string
  /** True when something is wired into the input. Lights the edge. */
  connected?: boolean
  onRun?: () => void
}

/**
 * A video on the board (Video Generation, 2026-10-08).
 *
 * Still until asked: the button in its corner plays it, with sound, and
 * stops it again (Huanrui, 2026-10-08 -- nothing plays by itself). No
 * browser controls: a press on a node is the start of a drag, and controls
 * would swallow it, so the picture would no longer move when grabbed. The
 * button keeps its press to itself for the same reason.
 */
function ClipPlayer({
  url,
  aspect,
  title,
  onLoaded,
}: {
  url: string
  aspect?: number
  title: string
  onLoaded: () => void
}) {
  const ref = useRef<HTMLVideoElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)
  const toggle = () => {
    const clip = ref.current
    if (!clip) return
    if (!clip.paused) {
      clip.pause()
      return
    }
    if (clip.ended) clip.currentTime = 0
    clip.muted = false
    void clip.play().catch((error: unknown) => console.warn('the video would not play', error))
  }
  return (
    <div className="cnode__clip" style={aspect ? { aspectRatio: String(aspect) } : undefined}>
      <video
        ref={ref}
        className="cnode__img"
        src={url}
        title={title}
        playsInline
        preload="metadata"
        onLoadedMetadata={onLoaded}
        onError={() => setFailed(true)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {failed ? <span className="cnode__clip-note">Could not load the video</span> : null}
      <button
        type="button"
        className="cnode__clip-play nodrag nopan"
        title={playing ? 'Pause' : 'Play'}
        aria-label={playing ? 'Pause' : 'Play'}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation()
          toggle()
        }}
      >
        {playing ? '❚❚' : '▶'}
      </button>
    </div>
  )
}

/** How long a full-size file stays after the board is zoomed back out past it. */
const RELEASE_FULL_MS = 1500

/**
 * Whether a picture needs drawing again: only when what it shows changed.
 *
 * React Flow also hands every node where it is on the board, which changes on
 * every frame of a drag; the picture does not use it (the board moves the
 * node's box itself), so it is left out here, and dragging a picture no longer
 * redraws it sixty times a second (2026-10-02).
 */
function sameImageProps(a: NodeProps, b: NodeProps): boolean {
  return a.id === b.id && a.data === b.data && a.selected === b.selected && a.width === b.width && a.height === b.height
}

export const ImageNode = memo(function ImageNode({ id, data, selected, width, height }: NodeProps) {
  const image = data as ImageNodeData
  /**
   * Where the ports are is worked out once, when the node is first drawn.
   *
   * A picture has no height until it has loaded, so a node drawn before that
   * is a strip of nothing and both ports are measured at its top edge. The
   * picture then arrives, the node grows, the ports move with it — and the
   * wires stay where the ports used to be, running out of the top corner.
   * Which is only visible on a board being opened fresh: make the node while
   * the picture is already in the cache and it has its height immediately,
   * which is why this never showed up while building one.
   */
  const remeasure = useUpdateNodeInternals()
  /**
   * Loading, patiently.
   *
   * A board asks the store for dozens of pictures at once, and the store
   * turns the odd one away and serves it a moment later. So a picture that
   * fails is asked for again, twice, a little later each time; then the
   * full-size file is tried the same way, if what failed was the smaller
   * copy; and only then does the box say it could not load -- with what the
   * store answered, so the next report says why rather than just "broken".
   */
  const [load, setLoad] = useState<{ which: 'url' | 'fallback' | 'dead'; tries: number; nonce: number }>({
    which: 'url',
    tries: 0,
    nonce: 0,
  })
  const [why, setWhy] = useState('')
  const timer = useRef(0)
  useEffect(() => {
    setLoad({ which: 'url', tries: 0, nonce: 0 })
    setWhy('')
  }, [image.url, image.fallback])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  /*
   * SHARP AT ANY ZOOM, the way Miro does it (2026-10-01).
   *
   * `url` is a copy made for drawing: 600 px for a library picture, up to
   * 1024 for a large project picture, the picture itself for a small one.
   * Once the board draws it larger on the screen than the copy really is, the
   * full-size file is laid over it. The line is the copy's OWN size, measured
   * when it loads -- it used to be a fixed 1100 px, which left the 600 px
   * library thumbnails stretched between 600 and 1100 and looking soft.
   *
   * The copy stays underneath the whole time: the full-size file fades in
   * over it once it has arrived, and if it never arrives the copy is simply
   * still there. Nothing goes blank or flickers on the way.
   *
   * LET GO AGAIN when the board is zoomed back out (2026-10-01): a decoded
   * 4000 x 3000 photograph holds about 48 MB, and keeping every one ever
   * zoomed into made a board of large pictures heavy to pan. A second and a
   * half after it is no longer needed it is dropped; zoomed into again, it
   * comes back from the browser's cache, so going back and forth is cheap.
   */
  const [copyEdge, setCopyEdge] = useState(0)
  // The drawing copy's own shape (width / height, as the file is), for a
  // picture turned a quarter whose file size is not known.
  const [natural, setNatural] = useState(0)
  /**
   * Turning by hand (Huanrui, 2026-10-06): the handle outside the bottom-left
   * corner is held and dragged round the picture's centre. While it is held
   * the picture follows the pointer freely, clicking into place within a few
   * degrees of a quarter turn; let go, and it settles on the nearest quarter
   * -- 90, 180, 270 or all the way round -- which is all the board keeps.
   */
  const [spin, setSpin] = useState<number | null>(null)
  const spinning = useRef<{ cx: number; cy: number; last: number; total: number } | null>(null)
  const [full, setFull] = useState<'no' | 'loading' | 'shown' | 'failed'>('no')
  // A new copy is measured again when it loads. A new link to the same
  // full-size file (they are renewed every ninety minutes) only means asking
  // for it again, if it is still wanted; the copy is untouched and keeps its
  // measurement, since it does not load again.
  useEffect(() => setCopyEdge(0), [image.url])
  useEffect(() => setFull('no'), [image.url, image.fallback])
  const canGoFull =
    load.which === 'url' &&
    Boolean(image.fallback) &&
    image.fallback !== image.url &&
    copyEdge > 0 &&
    !(image.fullEdge && copyEdge >= image.fullEdge * 0.98)
  const drawnLargerThanCopy = useStore(
    (state) =>
      canGoFull &&
      state.transform[2] * Math.max(width ?? 0, height ?? 0) * (window.devicePixelRatio || 1) > copyEdge * 1.02,
  )
  /*
   * Wanting it is not the same as having it (fullSize.ts, 2026-10-02): the
   * full-size file is fetched only once the board has been still for a
   * moment, and only the three pictures nearest the middle of the view hold
   * one at a time. Zoomed back out, it is let go a second and a half later,
   * so going back and forth does not thrash.
   */
  const [wantsFull, setWantsFull] = useState(false)
  useEffect(() => {
    if (drawnLargerThanCopy) {
      setWantsFull(true)
      return
    }
    const timer = window.setTimeout(() => setWantsFull(false), RELEASE_FULL_MS)
    return () => window.clearTimeout(timer)
  }, [drawnLargerThanCopy])
  const fullTurn = useFullSizeTurn(id, wantsFull)
  useEffect(() => {
    if (fullTurn && full === 'no') setFull('loading')
    if (!fullTurn && (full === 'shown' || full === 'loading')) setFull('no')
  }, [fullTurn, full])
  const src = load.which === 'fallback' ? image.fallback : image.url
  /*
   * A quarter turn either way swaps the box, not only the picture: the board
   * swaps the node's width and height when it turns it, so the box is the
   * shape of what is seen and the two ports stay at the middle of its left
   * and right sides. The picture is drawn at the box's other shape, centred,
   * and turned into it.
   */
  const turn = ((image.rotate ?? 0) % 360 + 360) % 360
  const sideways = turn === 90 || turn === 270
  const boxW = width ?? 0
  const boxH = height ?? 0
  // The box as drawn, for crop mode: a picture given only a width has no
  // height of its own until it is measured. Read only while cropping, so the
  // rest of the time no picture is redrawn for it.
  const measuredW = useStore((state) => (image.cropping ? state.nodeLookup.get(id)?.measured?.width ?? 0 : 0))
  const measuredH = useStore((state) => (image.cropping ? state.nodeLookup.get(id)?.measured?.height ?? 0 : 0))
  // A marked picture's ring and star stay the same size on screen at any
  // zoom, so a mark can be found on a board zoomed right out. Read only for
  // marked pictures: every other picture is left out of zooming.
  const markZoom = useStore((state) => (image.mark ? state.transform[2] : 1)) || 1
  const cropW = boxW || measuredW
  const cropH = boxH || measuredH
  /*
   * Turned a quarter (fixed 2026-10-16): drawn from the box the turn left
   * (its width and height swapped, handed in as boxShape), never from the
   * measured size, which lags a turn and every drag of a corner by a frame.
   * Drawn from that, the box stayed the old way round and the picture was
   * cover-cropped into it, zoomed in and soft, and resizing a turned picture
   * jumped. Percentages of the box, so nothing waits on a measurement: for a
   * box of shape r (width / height) the picture is drawn at 1/r of its width
   * and r of its height, centred, and turned into it.
   */
  const naturalShape = natural > 0 ? (sideways ? 1 / natural : natural) : 0
  const boardShape = image.boxShape || image.shape || naturalShape || (boxW && boxH ? boxW / boxH : 0)
  const turned: React.CSSProperties | undefined = sideways && boardShape
    ? {
        position: 'absolute',
        width: `${100 / boardShape}%`,
        height: `${boardShape * 100}%`,
        // The site-wide `max-width: 100%` on pictures would squash the turned
        // one back into the box before it is turned.
        maxWidth: 'none',
        maxHeight: 'none',
        left: `${((1 - 1 / boardShape) / 2) * 100}%`,
        top: `${((1 - boardShape) / 2) * 100}%`,
        transform: `rotate(${turn}deg)`,
      }
    : turn === 180
      ? { transform: 'rotate(180deg)' }
      : undefined
  const shaped = sideways ? undefined : image.shape ? { aspectRatio: String(image.shape) } : undefined
  // The ports move with the box when it turns; tell React Flow where they are now.
  useEffect(() => {
    remeasure(id)
  }, [id, turn, boxW, boxH, remeasure])

  const failedOnce = () => {
    if (load.tries < 2) {
      const tries = load.tries + 1
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(
        () => setLoad((was) => ({ ...was, tries, nonce: was.nonce + 1 })),
        900 * tries,
      )
      return
    }
    if (load.which === 'url' && image.fallback && image.fallback !== image.url) {
      setLoad((was) => ({ which: 'fallback', tries: 0, nonce: was.nonce + 1 }))
      return
    }
    setLoad((was) => ({ ...was, which: 'dead' }))
    // What the store actually said, for the box and for the console.
    const asked = src ?? ''
    void fetch(asked)
      .then((response) => {
        setWhy(response.ok ? `loads, but is not a picture (${response.headers.get('Content-Type') ?? '?'})` : `HTTP ${response.status}`)
        console.warn('a picture on the board would not load', {
          node: id,
          which: load.which,
          status: response.status,
          type: response.headers.get('Content-Type'),
        })
      })
      .catch((error: unknown) => {
        setWhy('no answer')
        console.warn('a picture on the board would not load', { node: id, which: load.which, error })
      })
  }

  return (
    <div
      className={`cnode cnode--image${selected ? ' cnode--selected' : ''}${image.mark ? ' cnode--marked' : ''}${spin !== null ? ' cnode--spinning' : ''}${image.cropping ? ' cnode--cropping nodrag' : ''}`}
      style={{
        ...(sideways && boardShape
          ? image.boxShape
            ? { width: '100%', height: '100%' }
            : { width: '100%', aspectRatio: String(boardShape) }
          : {}),
        ...(spin !== null ? { transform: `rotate(${spin}deg)` } : {}),
        ...(image.mark ? ({ '--mark-z': 1 / markZoom } as React.CSSProperties) : {}),
      }}
    >
      {/* Proportional only. Free resize on a photograph is a way to make a
          mistake, not a freedom — and locking the ratio is less code, because
          one dimension drives the other. */}
      <NodeResizer
        isVisible={selected && !image.cropping}
        keepAspectRatio
        minWidth={80}
        minHeight={80}
        lineClassName="cnode__resize-line"
        handleClassName="cnode__resize-handle"
      />

      {/* Left accepts the line a run draws from the skill; right feeds a skill.
          Named, and every edge names them back. An edge that does not say which
          handle it uses is drawn from the node's top-left corner instead of
          from the port — which is what happened to every wire on a board that
          had been saved and reopened, because saving dropped the names. */}
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      {image.video && image.url ? (
        <ClipPlayer url={image.fallback || image.url} aspect={image.aspect} title={image.title ?? ''} onLoaded={() => remeasure(id)} />
      ) : image.url && load.which !== 'dead' ? (
        <img
          key={load.nonce}
          className="cnode__img"
          src={src}
          alt={image.title ?? ''}
          draggable={false}
          style={{ ...shaped, ...turned }}
          onLoad={(event) => {
            const picture = event.currentTarget
            if (load.which === 'url') setCopyEdge(Math.max(picture.naturalWidth, picture.naturalHeight))
            if (picture.naturalWidth > 0 && picture.naturalHeight > 0) {
              setNatural(picture.naturalWidth / picture.naturalHeight)
              image.onShape?.(picture.naturalWidth / picture.naturalHeight)
            }
            remeasure(id)
          }}
          onError={failedOnce}
        />
      ) : image.url ? (
        <button
          type="button"
          className="cnode__broken nodrag"
          style={shaped}
          onClick={() => {
            setLoad((was) => ({ which: 'url', tries: 0, nonce: was.nonce + 1 }))
            setWhy('')
            image.onRetry?.()
          }}
        >
          Could not load{why ? ` (${why})` : ''} · Retry
        </button>
      ) : image.making ? (
        <div
          className="cnode__making"
          title="Making this one"
          style={image.aspect ? { aspectRatio: String(image.aspect) } : undefined}
        >
          {/* Deliberately not a spinner in the middle of an empty box. The
              frame is the picture's own outline, and the sweep across it says
              the space is spoken for — which is the thing somebody watching a
              board actually wants to know. */}
          {image.drawing ? (
            <WwLoaderFill label={image.clip ? 'Making a video — this takes a few minutes' : 'Making a picture'} size="large" />
          ) : (
            <span className="cnode__sweep" />
          )}
        </div>
      ) : (
        <div className="cnode__missing">
          {/* Not an error: the picture is fetched after the arrangement is
              drawn, so this is what a fraction of a second looks like. */}
          <span />
        </div>
      )}

      {/* The full-size file, over the copy, once the board is zoomed in past
          what the copy can show. See SHARP AT ANY ZOOM above. */}
      {image.url && load.which === 'url' && image.fallback && (full === 'loading' || full === 'shown') ? (
        <img
          className="cnode__img cnode__img--full"
          src={image.fallback}
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            ...turned,
            ...(sideways ? {} : { width: '100%', height: '100%' }),
            opacity: full === 'shown' ? 1 : 0,
            pointerEvents: 'none',
          }}
          // Decoded off to the side before it is shown, so the moment it fades
          // in is not also the moment a 4000-pixel photograph is unpacked.
          decoding="async"
          onLoad={(event) => {
            void event.currentTarget
              .decode()
              .catch(() => undefined)
              .then(() => setFull((was) => (was === 'loading' ? 'shown' : was)))
          }}
          onError={() => setFull('failed')}
        />
      ) : null}

      {/* The small white dot that marked a picture a skill made is gone
          (Huanrui, 2026-10-06): it was read as a fault in the picture, and the
          corner button already says how a picture was made. It was only ever
          drawn on the board, never in the file. */}
      {image.url && image.describe ? <MadeWithButton describe={image.describe} /> : null}
      {image.working ? (
        <div className="cnode__working">
          <WwLoaderFill label="Applying" size="large" />
        </div>
      ) : null}
      {image.mark ? (
        <span className="cnode__star" aria-label="Marked" title="Marked">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path d="M8 1.6l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.4l-3.8 2 .7-4.3-3.1-3 4.3-.6z" fill="currentColor" />
          </svg>
        </span>
      ) : null}
      {image.cropping && cropW > 0 && cropH > 0 && image.onCrop && image.onCropCancel ? (
        <CropBox
          width={cropW}
          height={cropH}
          onDone={(rect) => image.onCrop?.(rect, { w: cropW, h: cropH })}
          credits={image.expandCredits}
          onExpand={image.onExpand ? (rect, prompt) => image.onExpand?.(rect, { w: cropW, h: cropH }, prompt) : undefined}
          onCancel={() => image.onCropCancel?.()}
        />
      ) : null}
      {selected && image.solo && image.url && image.onRotate && !image.cropping ? (
        <button
          type="button"
          className="cnode__turn nodrag nopan"
          title="Hold and drag to turn (for looking at only; the picture is not changed)"
          aria-label="Hold and drag to turn"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => {
            if (event.button !== 0) return
            event.stopPropagation()
            event.preventDefault()
            const box = event.currentTarget.parentElement?.getBoundingClientRect()
            if (!box) return
            const cx = box.left + box.width / 2
            const cy = box.top + box.height / 2
            spinning.current = { cx, cy, last: Math.atan2(event.clientY - cy, event.clientX - cx), total: 0 }
            event.currentTarget.setPointerCapture(event.pointerId)
            setSpin(0)
          }}
          onPointerMove={(event) => {
            const held = spinning.current
            if (!held) return
            const now = Math.atan2(event.clientY - held.cy, event.clientX - held.cx)
            // Added up a step at a time, so going right round is 360 and not 0.
            let step = now - held.last
            if (step > Math.PI) step -= 2 * Math.PI
            if (step < -Math.PI) step += 2 * Math.PI
            held.last = now
            held.total += (step * 180) / Math.PI
            const nearest = Math.round(held.total / 90) * 90
            setSpin(Math.abs(held.total - nearest) < 8 ? nearest : held.total)
          }}
          onPointerUp={(event) => {
            const held = spinning.current
            spinning.current = null
            setSpin(null)
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId)
            }
            const quarters = held ? Math.round(held.total / 90) : 0
            if (quarters % 4 !== 0) image.onRotate?.(quarters)
          }}
          onPointerCancel={() => {
            spinning.current = null
            setSpin(null)
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M13 8a5 5 0 1 1-1.6-3.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M13 2.5V5.5H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : null}
    </div>
  )
}, sameImageProps)

export const SkillNode = memo(function SkillNode({ data, selected }: NodeProps) {
  const skill = data as SkillNodeData
  const lit = Boolean(skill.connected)

  return (
    <div
      className={[
        'cnode',
        'cnode--skill',
        selected ? 'cnode--selected' : '',
        lit ? 'cnode--lit' : '',
        skill.running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': skill.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <span className="cnode__glyph" aria-hidden="true">
        <SkillGlyph glyph={skill.glyph} />
      </span>
      <span className="cnode__name">{skill.name}</span>

      {skill.error ? <span className="cnode__error">{skill.error}</span> : null}

      <button
        type="button"
        className="cnode__run"
        onClick={() => skill.onRun?.()}
        disabled={skill.running || !lit}
        title={lit ? 'Run this skill' : 'Connect an image first'}
      >
        {skill.running ? 'Running…' : 'Run'}
      </button>
    </div>
  )
})
